import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronRightIcon, MapPinIcon } from '@heroicons/react/20/solid';
import { Link } from '@remix-run/react';

import type { CommunitySpace } from '~/interfaces/community';

import { ListDialog } from '../../ui/ListDialog';
import { PillTextInput } from '../../ui/PillTextInput';

import { useCityChannelStats } from '../../../_hooks/useCityChannelStats';
import { useCommunityMembership } from '../../../_hooks/useCommunityMembership';
import { useShortRelativeDate } from '../../../_hooks/useShortRelativeDate';
import { useSpaceAccess } from '../../../_hooks/useSpaceAccess';
import { useSpaceMembership } from '../../../_hooks/useSpaceMembership';

const SEARCHABLE_CITY_COUNT = 8;

interface Props {
  onClose: () => void;
  citySpaces: CommunitySpace[];
  hasCityChannel: boolean;
  spacePathFor: (space: CommunitySpace) => string | null;
}

export const BrowseCitiesDialog: FC<Props> = ({ onClose, citySpaces, hasCityChannel, spacePathFor }) => {
  const { t } = useTranslation();
  const { currentMember } = useCommunityMembership();
  const { isSpacePlaced } = useSpaceAccess();
  const { isJoined } = useSpaceMembership();
  const formatDate = useShortRelativeDate();
  const { data } = useCityChannelStats();
  const [search, setSearch] = useState('');

  const placedSpace = citySpaces.find((space) => isSpacePlaced(space));
  const memberCity = currentMember?.city;
  const statsById = new Map(data?.city_channels.map((stats) => [stats.id, stats]));
  const visibleSpaces = citySpaces.filter((space) =>
    `${space.name} ${space.slug}`.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const intro = () => {
    if (placedSpace) {
      return t('community.cities.intro_placed', {
        channel: placedSpace.slug,
        city: memberCity ?? placedSpace.city?.name,
      });
    }
    if (memberCity) return t('community.cities.intro_no_channel', { city: memberCity });

    return t('community.cities.intro_no_city');
  };

  const activity = (space: CommunitySpace) => {
    const stats = statsById.get(space.id);
    if (!stats) return null;

    return stats.last_posted_at
      ? t('community.cities.activity', {
          count: stats.member_count,
          time: formatDate(stats.last_posted_at, { withAgo: true }),
        })
      : t('community.cities.activity_no_posts', { count: stats.member_count });
  };

  const membershipLabel = (space: CommunitySpace) => {
    if (isSpacePlaced(space)) return t('community.cities.your_city');
    if (isJoined(space.slug)) return t('community.space.joined');

    return null;
  };

  return (
    <ListDialog
      isOpen
      onClose={onClose}
      title={hasCityChannel ? t('community.cities.browse') : t('community.cities.browse_city_channels')}
      header={
        <>
          <p className="mt-2 text-sm leading-5 text-black/60">{intro()}</p>
          {citySpaces.length > SEARCHABLE_CITY_COUNT && (
            <PillTextInput
              className="mt-4"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('community.cities.search')}
              aria-label={t('community.cities.search')}
            />
          )}
        </>
      }
    >
      <ul className="flex flex-col">
        {visibleSpaces.map((space) => {
          const path = spacePathFor(space);
          if (!path) return null;

          return (
            <li key={space.id}>
              <Link
                to={path}
                onClick={onClose}
                className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-black/5"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-black/5">
                  <MapPinIcon className="size-4 text-black/60" aria-hidden="true" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[15px] font-medium leading-5 text-black">#{space.slug}</span>
                  <span className="truncate text-[13px] leading-4 text-black/50">{activity(space)}</span>
                </span>
                <span className="shrink-0 text-[13px] text-black/50">{membershipLabel(space)}</span>
                <ChevronRightIcon className="size-4 shrink-0 text-black/30" aria-hidden="true" />
              </Link>
            </li>
          );
        })}
      </ul>
      {visibleSpaces.length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-black/50">{t('community.search.no_results')}</p>
      )}
    </ListDialog>
  );
};
